import { useFloating, offset, flip, shift, arrow, limitShift, autoUpdate, useDismiss, useInteractions } from '@floating-ui/react';
import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Popover.module.scss.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useControllableState } from '../util/useControllableState.js';
import { useA11y } from '../provider/A11yProvider.js';
import { Dialog } from '../dialog/Dialog.js';
import classNames from 'clsx';

function PopoverBase(props) {
  const {
    isOpen: controlledOpen,
    defaultOpen = false,
    onOpenChange,
    placement: idealPlacement = 'bottom',
    showTip = false,
    contents,
    variant,
    children,
    className: callerClassName,
    tag
  } = props;
  const [open, setOpen] = useControllableState(controlledOpen, defaultOpen, onOpenChange);
  const {
    portalTarget
  } = useA11y();
  const arrowRef = useRef(null);
  const {
    floatingStyles,
    refs,
    placement,
    middlewareData,
    context,
    elements,
    update
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: variant === 'dialog' ? `${idealPlacement}-start` : idealPlacement,
    strategy: 'fixed',
    middleware: [offset(8), flip({
      crossAxis: false
    }), shift({
      limiter: limitShift()
    }), showTip && arrow({
      element: arrowRef
    })]
  });
  useEffect(() => {
    if (open && elements.reference && elements.floating) {
      const cleanup = autoUpdate(elements.reference, elements.floating, update);
      return cleanup;
    }
  }, [open, elements, update]);
  const dismiss = useDismiss(context);
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([dismiss]);
  const className = classNames('dsx-component', styles.dialog, styles[`side-${placement.split('-')[0]}`], styles[`variant-${variant}`], callerClassName);
  function handleAnchorClick() {
    setOpen(!open);
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("span", {
      style: {
        display: 'inline-block'
      },
      ref: refs.setReference,
      "data-dsx--popover--anchor": tag,
      ...getReferenceProps({
        onClick: handleAnchorClick
      }),
      children: children
    }), portalTarget && /*#__PURE__*/createPortal(/*#__PURE__*/jsxs(Dialog, {
      focusOnOpen: false,
      isOpen: open,
      className: className,
      ref: refs.setFloating,
      style: floatingStyles,
      "data-dsx--popover": tag,
      children: [/*#__PURE__*/jsx("div", {
        className: styles.contents,
        ...getFloatingProps(),
        children: contents
      }), showTip && /*#__PURE__*/jsx("div", {
        className: styles.arrow,
        ref: arrowRef,
        style: {
          position: 'absolute',
          left: middlewareData.arrow?.x,
          top: middlewareData.arrow?.y
        }
      })]
    }), portalTarget)]
  });
}

export { PopoverBase };
